import axios from 'axios';
import { getConfig } from '@/config';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import type {
  SkinAnalysisPredictValidationRequest,
  SkinAnalysisPredictValidationResponse,
} from '@/models/SkinAnalysisPredictValidation';
import type { SkinAnalysisReportLimitResetResult } from '@/models/SkinAnalysisReportLimitReset';
import type {
  SkinAnalysisValidationPromptConfig,
  SkinAnalysisValidationPromptSaveRequest,
} from '@/models/SkinAnalysisValidationCopyPrompt';

export class SkinAnalysisPredictValidationClient {
  private static readonly TimeoutMs = 250_000;
  private static readonly CopyPromptTimeoutMs = 15_000;
  // The reset scans the whole Skin Analysis table (~1 min on prod).
  private static readonly ReportLimitResetTimeoutMs = 280_000;
  private readonly baseUrl = getAdminApiUrl(AdminApiDomain.SKIN_ANALYSIS_VALIDATION);

  public async predict(request: SkinAnalysisPredictValidationRequest): Promise<SkinAnalysisPredictValidationResponse> {
    const response = await axios.post<SkinAnalysisPredictValidationResponse>(
      `${this.baseUrl}/skinAnalysis/validation/predict`,
      request,
      {
        headers: { ...this.promptHeaders(), 'Content-Type': 'application/json' },
        timeout: SkinAnalysisPredictValidationClient.TimeoutMs,
      },
    );
    return response.data;
  }

  public async predictSonnet(
    request: SkinAnalysisPredictValidationRequest,
  ): Promise<SkinAnalysisPredictValidationResponse> {
    const response = await axios.post<SkinAnalysisPredictValidationResponse>(
      `${this.baseUrl}/skinAnalysis/validation/predictSonnet`,
      request,
      {
        headers: { ...this.promptHeaders(), 'Content-Type': 'application/json' },
        timeout: SkinAnalysisPredictValidationClient.TimeoutMs,
      },
    );
    return response.data;
  }

  public async getPrompts(): Promise<SkinAnalysisValidationPromptConfig> {
    const response = await axios.get<SkinAnalysisValidationPromptConfig>(this.promptsUrl(), {
      headers: this.promptHeaders(),
      timeout: SkinAnalysisPredictValidationClient.CopyPromptTimeoutMs,
    });
    return response.data;
  }

  public async savePrompts(
    request: SkinAnalysisValidationPromptSaveRequest,
  ): Promise<SkinAnalysisValidationPromptConfig> {
    const response = await axios.put<SkinAnalysisValidationPromptConfig>(this.promptsUrl(), request, {
      headers: { ...this.promptHeaders(), 'Content-Type': 'application/json' },
      timeout: SkinAnalysisPredictValidationClient.CopyPromptTimeoutMs,
    });
    return response.data;
  }

  public async resetPrompts(): Promise<SkinAnalysisValidationPromptConfig> {
    const response = await axios.delete<SkinAnalysisValidationPromptConfig>(this.promptsUrl(), {
      headers: this.promptHeaders(),
      timeout: SkinAnalysisPredictValidationClient.CopyPromptTimeoutMs,
    });
    return response.data;
  }

  public async resetReportLimit(email: string): Promise<SkinAnalysisReportLimitResetResult> {
    const response = await axios.post<SkinAnalysisReportLimitResetResult>(
      `${this.baseUrl}/skinAnalysis/admin/reportLimit/reset`,
      { email },
      {
        headers: { ...this.promptHeaders(), 'Content-Type': 'application/json' },
        timeout: SkinAnalysisPredictValidationClient.ReportLimitResetTimeoutMs,
      },
    );
    return response.data;
  }

  private promptsUrl(): string {
    return `${this.baseUrl}/skinAnalysis/validation/prompts`;
  }

  private promptHeaders(): Record<string, string> {
    return { 'X-API-KEY': getConfig().apiKey };
  }
}
